import { Pipe, PipeTransform } from "@angular/core";
import { DomSanitizer, SafeHtml, SafeResourceUrl, SafeStyle, SafeUrl } from "@angular/platform-browser";

import dayjs from "dayjs";

@Pipe({ name: 'DefaultTextOrValue' })
export class DefaultTextOrValue implements PipeTransform {
    /**
     * Custom Pipe para formatação de valores undefined/null que possam ser binded numa view.
     * Caso o valor seja undefined/null/"" o valor é replaced pela string default: "Sem Informação".
     * Caso seja necessário, pode ser passado o defaultText como parametro no pipe com um texto personalizado.
     * @param value valor a ser renderizado
     * @param defaultText valor string para replace caso o "valor" seja undefined/null.
     * @returns string
     */
    transform(value: any, defaultText: string = "Sem Informação"): any {
        if (!value) return defaultText;

        return value;
    }
}

@Pipe({ name: 'DefaultTextOrArrayValue' })
export class DefaultTextOrArrayValue implements PipeTransform {
    /**
     * Custom Pipe para formatação de valores em listas de strings que possam ser binded numa view.
     * Caso o valor seja undefined/null/ou não existem valores, o valor é replaced pela string default: "Sem Informação".
     * Todos os valores undefined/null são removidos do array de valores antes do tratamento.
     * Caso seja necessário, pode ser passado o defaultText como parametro no pipe com um texto personalizado.
     * @param value array valor a ser renderizado
     * @param defaultText valor string para replace caso o "valor" seja undefined/null.
     * @returns string
     */
    transform(value: any[], defaultText: string = "Sem Informação"): any {
        if (!value) return defaultText;

        const stringValue = value.filter(x => x).join(', ');

        if (!stringValue) return defaultText;

        return stringValue;
    }
}

@Pipe({ name: 'DateFromNow' })
export class DateFromNow implements PipeTransform {
    /**
     * Custom Pipe para formatação de datas com tempo relativo.
     * Por exemplo "à 2 minutos", "à 3 dias", etc.
     * @param value data string ou Date Object.
     * @returns string
     */
    transform(value: string | Date): string {
        if (!value) return "Sem informação";

        const date = dayjs(value);

        if (!date.isValid()) return "Sem informação";

        const relative = date.fromNow();

        return relative;
    }
}

@Pipe({ name: 'SanitizeHtml' })
export class SanitizeHTML {
    constructor(private sanitizer: DomSanitizer) { }

    /**
     * Pipe que marca string HTML como segura para injeção e bind em elemento DOM.
     * @param value 
     * @returns 
     */
    transform(value) {
        return this.sanitizer.bypassSecurityTrustHtml(value);
    }
}


@Pipe({ name: 'FilterByFields' })
export class FilterByFields implements PipeTransform {
    /**
     * 
     * @param array 
     * @param text 
     * @param field 
     * @returns 
     */
    transform(array: any[], text: string, fields: string[] = ["Description", "Code"]): any {
        if (text)
            array = array.filter(it => fields.some(field => it[field].toLowerCase().includes(text.toLocaleLowerCase())))

        return array;
    }
}


@Pipe({
    name: 'security'
})
export class SecurityPipe implements PipeTransform {

    url: SafeResourceUrl;
    constructor(protected sanitizer: DomSanitizer) {

    }

    public transform(value: string, type: string): SafeHtml | SafeStyle | SafeUrl | SafeResourceUrl {

        switch (type) {
            case 'html':
                return this.sanitizer.bypassSecurityTrustHtml(value);
            case 'style':
                return this.sanitizer.bypassSecurityTrustStyle(value);
            case 'script':
                return this.sanitizer.bypassSecurityTrustScript(value);
            case 'url':
                this.url = this.sanitizer.bypassSecurityTrustUrl(value);
                return this.url;
            case 'resourceUrl':
                this.url = this.sanitizer.bypassSecurityTrustResourceUrl(value);
                return this.url;

            default: throw new Error(`Invalid safe type specified: ${type}`);
        }
    }

}


@Pipe({ name: 'searchValueProperty' })
export class SearchValuePropertyPipe implements PipeTransform {
    transform(array: Array<any>, arg: string, property: any = 'Name', prop: any = null): any {
        console.log(property);
        let a: Array<any> = array ? [...[], ...array] : [];

        if (prop) {
            a = a.filter(item => arg && item[property][prop] ? item[property][prop].toLowerCase().match(arg.toLowerCase().trim()) : item);
            return a;
        }

        a = a.filter(item => arg && item[property] ? item[property].toLowerCase().match(arg.toLowerCase().trim()) : item);

        return a;
    }
}


@Pipe({
    name: 'currencyEuro'
})
export class CurrencyEuroPipe implements PipeTransform {
    transform(value: number, decimal: number = 2, min: number = 0): string | number {
        const formatter = new Intl.NumberFormat('pt-PT', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: min,
            maximumFractionDigits: decimal,
        });
        return value ? formatter.format(value).replace(',', '.') : 0;
    }
}

@Pipe({
    name: 'maskNIF'
})
export class MaskVatNumberPipe implements PipeTransform {
    transform(value: string): string {

        return value ? `xxxxx${(value).substring(value.length - 4)}` : 'xxxxxxxxxxx';
    }
}

@Pipe({
    name: 'maskZeroNumber'
})
export class MaskZeroNumberPipe implements PipeTransform {
    transform(value: string, pad: number = 8): string {

        if (value === null || value === undefined) {
            return '';
        }

        // Converte para string – garante que números e strings numéricas sejam tratados da mesma forma.
        const numericString = String(value).trim();

        // Verifica se o conteúdo já tem n ou mais caracteres.
        if (numericString.length >= pad) {
            return numericString;
        }

        // Preenche com zeros à esquerda até atingir n caracteres.
        const padded = numericString.padStart(pad, '0');
        return padded;
    }
}

@Pipe({
    name: 'currencyFormatter'
})
export class CurrencyFormatterPipe implements PipeTransform {

    transform(value: string | number | null | undefined): string {
        if (value === null || value === undefined) {
            return '';
        }

        try {

            // Converte para string
            const stringValue = String(value);


            if (!stringValue.includes('€')) return stringValue;

            // Remove símbolo € e espaços
            const cleanValue = stringValue.replace('€', '').replace(/\s/g, '').trim();

            if (cleanValue === '') {
                return '';
            }

            // Substitui vírgula por ponto e converte para número
            const numericValue = parseFloat(cleanValue.replace(',', '.'));

            if (isNaN(numericValue)) {
                return stringValue;
            }

            // Formatação manual com pontos como separadores de milhares
            const parts = numericValue.toFixed(2).split('.');
            const integerPart = parts[0];
            const decimalPart = parts[1];

            // Adiciona pontos como separadores de milhares
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

            return `${formattedInteger},${decimalPart} €`;

        } catch (error) {
            console.warn('Erro ao formatar valor monetário:', value, error);
            return String(value);
        }
    }
}

@Pipe({ name: 'jsontree' })
export class JsonTreeValuePipe implements PipeTransform {
    transform(value: any): string {
        if (value === null) return 'null';
        if (typeof value === 'string') {
            return value.length > 30 ? `"${value.substring(0, 30)}..."` : `"${value}"`;
        }
        if (typeof value === 'object') {
            if (Array.isArray(value)) return `(${value.length} itens)`;
            return `{ ${Object.keys(value).length} chaves }`;
        }
        return String(value);
    }
}